// Auth orchestration: owns sign-in/out actions and translates Supabase session
// changes into the phase-1 sync wiring. On sign-in it connects a SupabaseBackend
// to the shared syncController (which drains the outbox — local writes mirror
// up); on sign-out it disconnects (queued entries persist for next time).
//
// Every entry point is a no-op when Supabase isn't configured, so importing this
// from main.ts is safe in a local-only build.
//
// Sign-in drives the controller's full connect sequence (claim → pull+merge →
// push); the claim prompt itself is registered by the UI (auth-panel).

import { syncController } from "../sync/controller";
import { SupabaseBackend } from "../sync/supabase-backend";
import { setActiveScope, wipeScopeLocalData } from "../sync/account-scope";
import { getSupabaseClient, isSupabaseConfigured } from "./supabase-client";
import { firePasswordRecovery } from "./recovery";

export interface AuthUser {
  id: string;
  email: string;
}

type AuthListener = (user: AuthUser | null) => void;

const listeners = new Set<AuthListener>();
let current: AuthUser | null = null;
let started = false;

function setUser(user: AuthUser | null): void {
  current = user;
  for (const cb of listeners) cb(user);
}

// Wire (or unwire) sync to match the session — and swap the local-persistence
// scope so each account (and signed-out use) sees only its own pool. main
// reacts to the scope change by resetting the open documents + the dock.
async function applySession(user: AuthUser | null, awaitSync = false): Promise<void> {
  let connecting: Promise<void> | null = null;
  if (user) {
    // Scope BEFORE connect, so the pull merges the account's docs into the
    // account's own pool, never the anonymous one.
    setActiveScope(user.id);
    const client = await getSupabaseClient();
    // connect runs the full sequence: claim (first login here) → pull+merge the
    // account's docs down → flush local writes up. userId keys the pull cursor.
    // Normally NOT awaited — the UI flips to signed-in immediately while sync
    // proceeds.
    connecting = syncController.connect(new SupabaseBackend(client, user.id), user.id);
    if (!awaitSync) void connecting;
  } else {
    // Disconnect BEFORE re-scoping: in-flight pulls/flushes are pinned to the
    // account's pool and bail once the backend detaches.
    syncController.disconnect();
    setActiveScope(null);
  }
  setUser(user);
  // Boot only: let the first pull land before returning, so whoever opens the
  // boot document sees the account's own layouts rather than an empty pool.
  if (awaitSync && connecting) await connecting.catch(() => {});
}

function toUser(sessionUser: { id: string; email?: string } | undefined | null): AuthUser | null {
  return sessionUser ? { id: sessionUser.id, email: sessionUser.email ?? "" } : null;
}

// Boot settle latch. Resolves once a persisted session (if any) has been
// applied AND its first sync connect has finished — i.e. the active scope is
// final and its local pool is populated. main awaits this before opening the
// boot document, so a restoring session can never blank a scene we already put
// on screen. Resolves immediately when Supabase isn't configured (no auth will
// ever run), and in a `finally` so a failed init can't strand boot.
let settleBoot: () => void = () => {};
const bootSettled = new Promise<void>((res) => {
  settleBoot = res;
});
export function authSettled(): Promise<void> {
  return isSupabaseConfigured() ? bootSettled : Promise.resolve();
}

// Whether a persisted session might restore on this boot — a cheap synchronous
// peek at supabase-js's `sb-<ref>-auth-token` localStorage entry. Boot uses it
// to decide whether it must WAIT for auth before opening a document: only a
// restoring session triggers the scope change that would blank an already-open
// scene, so signed-out users (the common case) shouldn't pay for loading the
// auth client at all. Reading the key convention rather than the session is
// deliberate — the real check costs a dynamic import of supabase-js, which is
// the very latency this avoids. Both ways of being wrong are benign: a false
// positive just waits (bounded by the caller's timeout), a false negative is
// no worse than not having this gate.
export function hasPersistedSession(): boolean {
  if (!isSupabaseConfigured()) return false;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      // supabase-js v2 default storageKey; a large session is chunked into
      // `…-auth-token.0`, `.1`, so accept the suffixed form too.
      if (k && /^sb-.+-auth-token(\.\d+)?$/.test(k)) return true;
    }
  } catch {
    /* storage blocked (private mode) — assume signed out */
  }
  return false;
}

// Boot: restore any persisted session and start reacting to auth changes.
// Idempotent and inert when unconfigured.
export async function initAuth(): Promise<void> {
  if (started || !isSupabaseConfigured()) return;
  started = true;
  try {
    const client = await getSupabaseClient();
    client.auth.onAuthStateChange((event, session) => {
      // A recovery deep-link signs the user in AND flags the session as a
      // password recovery: applySession runs the normal sign-in wiring below,
      // and the latch tells the UI to prompt for a new password. (The panel
      // registers its handler before initAuth; the latch buffers a race.)
      if (event === "PASSWORD_RECOVERY") firePasswordRecovery();
      void applySession(toUser(session?.user));
    });
    const { data } = await client.auth.getSession();
    await applySession(toUser(data.session?.user), true);
  } finally {
    settleBoot();
  }
}

// ── Subscriptions / accessors (for the UI) ──────────────────────────────────
export function onAuthState(cb: AuthListener): () => void {
  listeners.add(cb);
  cb(current); // fire current state immediately
  return () => listeners.delete(cb);
}
export function currentUser(): AuthUser | null {
  return current;
}

// ── Actions ─────────────────────────────────────────────────────────────────
// Each rejects with a human-readable message the UI surfaces via showAlert.
async function client() {
  return getSupabaseClient();
}
function fail(prefix: string, message: string): never {
  throw new Error(`${prefix}: ${message}`);
}

// Switching accounts swaps the local document pool, which resets the open
// documents — so the interactive auth actions first run this guard (main
// registers the "save your unsaved work?" flow). Resolving false cancels the
// action. Redirect-based sign-ins (OAuth, magic link) reload the page and are
// covered by the beforeunload prompt instead; a server-side session expiry has
// no interception point (it behaves like a reload).
let scopeSwitchGuard: (() => Promise<boolean>) | null = null;
export function setScopeSwitchGuard(guard: () => Promise<boolean>): void {
  scopeSwitchGuard = guard;
}
async function guardScopeSwitch(): Promise<boolean> {
  return scopeSwitchGuard ? scopeSwitchGuard() : true;
}

export async function signInWithPassword(email: string, password: string): Promise<void> {
  if (!(await guardScopeSwitch())) return; // user cancelled — stay signed out
  const { error } = await (await client()).auth.signInWithPassword({ email, password });
  if (error) fail("Sign in failed", error.message);
}

// What a signUp call produced. With email confirmation ON (the live project's
// setting), Supabase returns a user but NO session: the account exists but
// nobody is signed in until the confirmation link is clicked — at which point
// a SIGNED_IN event arrives via onAuthStateChange and applySession runs the
// scope swap + sync connect. Nothing here touches scope directly, so the
// "confirm-email" outcome leaves local persistence exactly as it was.
export type SignUpOutcome = "cancelled" | "session" | "confirm-email";

// Pure classification of auth.signUp's data payload. (When confirmation is on
// and the email is already registered, Supabase anti-enumeration returns a
// stub user with no session — that also classifies as "confirm-email", which
// is the correct thing to show.) Exported for tests.
export function classifySignUp(data: { session: unknown } | null | undefined): "session" | "confirm-email" {
  return data?.session ? "session" : "confirm-email";
}

export async function signUpWithPassword(email: string, password: string): Promise<SignUpOutcome> {
  // Guarded like sign-in: with email confirmation OFF, signUp starts a session
  // immediately (and with it the scope swap). With confirmation ON the guard
  // runs but no swap follows — acceptable, since we can't know the project
  // setting before calling.
  if (!(await guardScopeSwitch())) return "cancelled";
  const { data, error } = await (await client()).auth.signUp({ email, password });
  if (error) fail("Sign up failed", error.message);
  return classifySignUp(data);
}

// Emails a password-reset link that returns to this origin; clicking it signs
// the user in with a session flagged PASSWORD_RECOVERY (handled in initAuth).
export async function requestPasswordReset(email: string): Promise<void> {
  const { error } = await (await client()).auth.resetPasswordForEmail(email, {
    redirectTo: window.location.origin,
  });
  if (error) fail("Password reset failed", error.message);
}

// Set a new password for the currently signed-in user (the recovery-link
// session, in the reset flow).
export async function updatePassword(password: string): Promise<void> {
  const { error } = await (await client()).auth.updateUser({ password });
  if (error) fail("Password update failed", error.message);
}

export async function signOut(): Promise<void> {
  // The guard runs while still signed in, so a "Save" lands in the account's
  // pool (and its queued push survives in that account's outbox).
  if (!(await guardScopeSwitch())) return;
  const { error } = await (await client()).auth.signOut();
  if (error) fail("Sign out failed", error.message);
}

// Permanently delete the signed-in account and everything server-side, then
// clear the account's local footprint. The caller owns the confirmation UI;
// this assumes the user already said yes.
export async function deleteAccount(): Promise<void> {
  if (!(await guardScopeSwitch())) return; // last chance to save into the pool
  const user = current;
  if (!user) fail("Account deletion failed", "no user is signed in");
  const c = await client();
  // Stop syncing first: a flush racing the deletion would only error against
  // the dying account.
  syncController.disconnect();
  // Thumbnails go through the storage API so the backing files are deleted,
  // not just their rows (the RPC repeats the row delete as a safety net).
  const { data: files } = await c.storage.from("thumbnails").list(user.id);
  if (files && files.length > 0) {
    await c.storage
      .from("thumbnails")
      .remove(files.map((f) => `${user.id}/${f.name}`))
      .catch(() => {}); // best-effort; the RPC still removes the rows
  }
  // Server-side point of no return: drops the auth user; models/layouts rows
  // cascade (see supabase/migrations/0004_delete_account.sql).
  const { error } = await c.rpc("delete_account");
  if (error) fail("Account deletion failed", error.message);
  // The account no longer exists, so the sign-out call may 4xx — ignore it;
  // the SIGNED_OUT event still fires and applySession swaps back to the
  // anonymous pool.
  await c.auth.signOut().catch(() => {});
  // Finally, remove the departed account's local libraries + outbox.
  await wipeScopeLocalData(user.id);
}
